import { AppThunk } from "@app/redux/store";
import { Authenticate } from "@app/providers/auth/auth";
import { IOutFeaturedDealDTO } from "@app/components/deal-cards/new-deal-card";
import { showToast } from "@app/providers/toast-provider/default-toast/default-toast";
import { formatMessage } from "@app/translations/intl";
import { PublicUsersApi } from "@app/bf-api";

import { authReducer } from "../reducers/auth";

export interface IMerchantResult {
  merchants: IOutFeaturedDealDTO[];
  totalResults: number;
}

const fetchUser =
  (jwt?: string): AppThunk =>
  async (dispatch) => {
    try {
      if (!jwt) {
        dispatch(authReducer.setUser(undefined));
        return;
      }

      dispatch(authReducer.setIsLoading(true));
      const user = await Authenticate.getMe(jwt);

      dispatch(authReducer.setUser(user));
      dispatch(authReducer.setLikedEans(user?.dataWarehouseProduct.map((deal) => deal.ean ?? "") ?? []));

      return user;
    } catch (error) {
      dispatch(authReducer.setUser(undefined));
    }
  };

const authenticate = (): AppThunk => async (dispatch) => {
  const jwt = await Authenticate.getJWT();
  dispatch(fetchUser(jwt));
};

const login =
  (jwt: string): AppThunk =>
  async (dispatch) => {
    try {
      const loginResult = await Authenticate.login(jwt);
      dispatch(fetchUser(loginResult?.publicToken));
    } catch (error) {
      dispatch(authReducer.setUser(undefined));
    }
  };

const logout = (): AppThunk => (dispatch) => {
  Authenticate.logout();
  dispatch(authReducer.reset());
};

const addLikedDeal =
  (ean?: string, refetchUser?: boolean): AppThunk =>
  async (dispatch, getState) => {
    try {
      const jwt = await Authenticate.getJWT();

      if (!ean || !jwt) {
        return;
      }

      const api = new PublicUsersApi();
      const updatedUser = await api.likeEan(
        { productAlertDTO: { ean } },
        { headers: { "x-access-token": jwt, "Content-Type": "application/json" } }
      );

      dispatch(authReducer.setLikedEans(updatedUser.deals.map((deal) => deal.ean).filter(Boolean)));

      if (refetchUser) {
        dispatch(fetchUser(jwt));
      }

      showToast({
        title: formatMessage({ id: "global.dealAlerts.notification.add.success.title" }),
        description: formatMessage({ id: "global.dealAlerts.notification.add.success.description" }),
        iconType: "success"
      });
    } catch {
      showToast({
        title: formatMessage({ id: "global.dealAlerts.notification.error.title" }),
        description: formatMessage({ id: "global.dealAlerts.notification.error.description" }),
        iconType: "error"
      });
    }
  };

const removeLikedDeal =
  (ean?: string): AppThunk =>
  async (dispatch, getState) => {
    try {
      const jwt = await Authenticate.getJWT();

      if (!ean || !jwt) {
        return;
      }

      const api = new PublicUsersApi();
      await api.dislikeEan(
        { productAlertDTO: { ean } },
        { headers: { "x-access-token": jwt, "Content-Type": "application/json" } }
      );
      dispatch(authReducer.removeLikedDeal(ean));

      showToast({
        title: formatMessage({ id: "global.dealAlerts.notification.remove.success.title" }),
        description: formatMessage({ id: "global.dealAlerts.notification.remove.success.description" }),
        iconType: "success"
      });
    } catch {
      showToast({
        title: formatMessage({ id: "global.dealAlerts.notification.error.title" }),
        description: formatMessage({ id: "global.dealAlerts.notification.error.description" }),
        iconType: "error"
      });
    }
  };

export const authThunks = {
  fetchUser,
  authenticate,
  login,
  logout,
  addLikedDeal,
  removeLikedDeal
};
